import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Typography, Tabs, Tab } from "@mui/material";
import SearchBar from "../../components/SearchBar/SearchBar";
import {
  learnLibraryAllCourses,
  learnLibraryAllPolicies,
  learnLibraryMyItems,
  learnLibraryPolicies,
  learnLibrary,
  buildLibrarySearchRoute
} from "../../routes/LearningCenterRoutes";
import FullPageLayout from "../../components/FullPageLayout/index";
import { useTranslation } from "react-i18next";
import { push } from "connected-react-router";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Route, Redirect } from "react-router-dom";
import AllCourses from "../../views/Library/AllCourses/AllCourses";
import Policies from "../../views/Library/Policies/Policies";
import MyItems from "../../views/Library/MyItems/MyItems";
import usePermissions from "../../redux/selectors/usePermissions";
import CoursesListContainer from "./Library/AllCourses/CoursesListContainer";
import { isIframed } from "../../utils/iframe";

const useStyles = makeStyles(theme => ({
  tab: {
    [theme.breakpoints.down("md")]: {
      flex: 1
    }
  }
}));

const getActiveTab = (tabs, pathname) => {
  const currentPath = tabs.find(({ path }) => pathname.includes(path));
  return currentPath ? currentPath.path : learnLibraryAllCourses;
};

/**
 * Returns the tab for the views in the library page. Does not return
 * the tab for the Policies view if the policyPartnerUrl is not
 * present in local storage
 */
const getLibraryTab = (path, title, classes, hasPolicyPartnerLearnerAccess) => {
  const libraryTab = (
    <Tab
      value={path}
      key={path}
      label={<Typography variant="body1">{title}</Typography>}
      className={classes.tab}
    />
  );
  if (path === learnLibraryPolicies) {
    return localStorage.getItem("policyPartnerUrl") !== "" ? libraryTab : null;
  } else if (path === learnLibraryAllPolicies) {
    return hasPolicyPartnerLearnerAccess ? libraryTab : null;
  } else {
    return libraryTab;
  }
};

const LearningCenterLibrary = ({ location: { pathname }, push }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hasPolicyPartnerLearnerAccess } = usePermissions();
  const classes = useStyles();

  const tabs = [
    {
      title: t("learningCenterLibrary.allCourses"),
      path: learnLibraryAllCourses
    },
    {
      title: t("learningCenterLibrary.policies"),
      path: learnLibraryPolicies
    },
    {
      title: t("learningCenterLibrary.allPolicies"),
      path: learnLibraryAllPolicies
    },
    {
      title: t("learningCenterLibrary.myItems"),
      path: learnLibraryMyItems
    }
  ];

  const [activeTab, setActiveTab] = React.useState(
    getActiveTab(tabs, pathname)
  );

  useEffect(() => {
    setActiveTab(getActiveTab(tabs, pathname));
  }, [pathname]);

  function handleTabChange(e, newTabValue) {
    push(newTabValue);
    setActiveTab(newTabValue);
  }

  function handleSearchSubmit(searchValue) {
    if (searchValue) {
      push(buildLibrarySearchRoute(searchValue));
    }
  }

  const searchBar = (
    <SearchBar
      background={theme.palette.inputBackground}
      placeholder={
        isIframed
          ? t("learningCenterLibraryIframe.searchPlaceholder")
          : t("learningCenterLibrary.searchPlaceholder")
      }
      onSearchSubmit={handleSearchSubmit}
    />
  );
  return (
    <FullPageLayout
      headerActions={searchBar}
      title={isIframed ? " " : t("learningCenterLibrary.title")}
    >
      {!isIframed && (
        <Tabs
          value={activeTab}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleTabChange}
        >
          {tabs.map(tab =>
            getLibraryTab(
              tab.path,
              tab.title,
              classes,
              hasPolicyPartnerLearnerAccess
            )
          )}
        </Tabs>
      )}
      <Route
        exact
        path={learnLibrary}
        render={() => <Redirect to={learnLibraryAllCourses} />}
      />
      <Route path={learnLibraryAllCourses} component={AllCourses} />
      {localStorage.getItem("policyPartnerUrl") !== "" && (
        <Route path={learnLibraryPolicies} component={Policies} />
      )}
      <Route path={learnLibraryMyItems} component={MyItems} />
      <Route path={learnLibraryAllPolicies} component={CoursesListContainer} />
    </FullPageLayout>
  );
};

const mapStateToProps = state => {
  return {
    user: state.userState.user || { permissions: {} }
  };
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ push }, dispatch);
}

LearningCenterLibrary.propTypes = {
  match: PropTypes.any,
  history: PropTypes.any
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LearningCenterLibrary);
